// components/GestionMots.jsx
// Composant réutilisable pour la gestion des mots (signalés ou recherche)

import React, { useState } from 'react';
import { CheckCircle, Edit, Trash2, X, Save, Loader2 } from 'lucide-react';
import { useAuth } from '../contexts/AuthContext';

export default function GestionMots({
  mots,
  loading,
  error,
  onReload,
  showValiderButton = false,
  contextes = [],
  emptyMessage = 'Aucun mot trouvé',
  emptyHint = '',
  langueCibleManquante = null, // Langue vers laquelle la traduction manque
}) {
  const { user } = useAuth();
  const [editingMot, setEditingMot] = useState(null);
  const [formData, setFormData] = useState({});
  const [editingTraduction, setEditingTraduction] = useState(null);
  const [traductionFormData, setTraductionFormData] = useState({});
  const [addingSynonymeFor, setAddingSynonymeFor] = useState(null);
  const [synonymeFormData, setSynonymeFormData] = useState({
    motSingulier: '',
    motPluriel: '',
    genre: '',
    type: 'nom',
    contexte: '',
  });

  // Marquer un mot comme corrigé
  const handleMarquerCorrige = async (motId) => {
    const confirmation = window.confirm(
      'Marquer ce mot comme corrigé ?\n\nIl redeviendra visible dans les exercices.'
    );

    if (!confirmation) return;

    try {
      const response = await fetch(`/api/mots/${motId}/corriger`, {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json' },
      });

      const data = await response.json();

      if (data.success) {
        onReload();
      } else {
        throw new Error(data.error || 'Erreur lors de la correction');
      }
    } catch (err) {
      console.error('Erreur correction:', err);
      alert('❌ Erreur : ' + err.message);
    }
  };

  // Supprimer un mot
  const handleSupprimerMot = async (motId) => {
    const confirmation = window.confirm(
      '⚠️ ATTENTION : Supprimer ce mot ?\n\n' +
      'Cette action est irréversible et supprimera aussi toutes ses traductions.'
    );

    if (!confirmation) return;

    try {
      const response = await fetch(`/api/mots/${motId}`, {
        method: 'DELETE',
      });

      const data = await response.json();

      if (data.success) {
        onReload();
      } else {
        throw new Error(data.error || 'Erreur lors de la suppression');
      }
    } catch (err) {
      console.error('Erreur suppression:', err);
      alert('❌ Erreur : ' + err.message);
    }
  };

  // Démarrer l'édition d'un mot
  const handleStartEdit = (mot) => {
    setEditingMot(mot.id);
    setFormData({
      motSingulier: mot.motSingulier || '',
      motPluriel: mot.motPluriel || '',
      genre: mot.genre || '',
      type: mot.type || 'nom',
      sousType: mot.sousType || '',
      contexte: mot.contexte || '',
      estFauxAmi: mot.estFauxAmi || false,
      nombre: mot.nombre || '',
    });
  };

  // Annuler l'édition
  const handleCancelEdit = () => {
    setEditingMot(null);
    setFormData({});
  };

  // Sauvegarder les modifications
  const handleSaveEdit = async (motId) => {
    try {
      const response = await fetch(`/api/mots/${motId}`, {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(formData),
      });

      const data = await response.json();

      if (data.success) {
        alert('✅ Mot modifié avec succès');
        setEditingMot(null);
        setFormData({});
        onReload();
      } else {
        throw new Error(data.error || 'Erreur lors de la modification');
      }
    } catch (err) {
      console.error('Erreur modification:', err);
      alert('❌ Erreur : ' + err.message);
    }
  };

  // Démarrer l'édition d'une traduction
  const handleStartEditTraduction = (traductionId, motCible) => {
    setEditingTraduction(traductionId);
    setTraductionFormData({
      motSingulier: motCible.motSingulier || '',
      motPluriel: motCible.motPluriel || '',
      genre: motCible.genre || '',
      type: motCible.type || 'nom',
      sousType: motCible.sousType || '',
      contexte: motCible.contexte || '',
    });
  };

  // Annuler l'édition d'une traduction
  const handleCancelEditTraduction = () => {
    setEditingTraduction(null);
    setTraductionFormData({});
  };

  // Sauvegarder les modifications d'une traduction
  const handleSaveEditTraduction = async (motCibleId) => {
    try {
      const response = await fetch(`/api/mots/${motCibleId}`, {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(traductionFormData),
      });

      const data = await response.json();

      if (data.success) {
        alert('✅ Traduction modifiée avec succès');
        setEditingTraduction(null);
        setTraductionFormData({});
        onReload();
      } else {
        throw new Error(data.error || 'Erreur lors de la modification');
      }
    } catch (err) {
      console.error('Erreur modification traduction:', err);
      alert('❌ Erreur : ' + err.message);
    }
  };

  // Supprimer une traduction
  const handleSupprimerTraduction = async (traductionId) => {
    const confirmation = window.confirm(
      '⚠️ Supprimer cette traduction ?\n\n' +
      'Cette action est irréversible.'
    );

    if (!confirmation) return;

    try {
      const response = await fetch(`/api/traductions/${traductionId}`, {
        method: 'DELETE',
      });

      const data = await response.json();

      if (data.success) {
        alert('✅ Traduction supprimée avec succès');
        onReload();
      } else {
        throw new Error(data.error || 'Erreur lors de la suppression');
      }
    } catch (err) {
      console.error('Erreur suppression traduction:', err);
      alert('❌ Erreur : ' + err.message);
    }
  };

  // Créer une traduction inverse manquante
  const handleCreerTraductionInverse = async (motSourceId, motCibleId) => {
    try {
      const response = await fetch('/api/traductions', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          motSourceId: motCibleId,
          motCibleId: motSourceId,
        }),
      });

      const data = await response.json();

      if (data.success) {
        alert('✅ Traduction inverse créée avec succès');
        onReload();
      } else {
        throw new Error(data.error || 'Erreur lors de la création');
      }
    } catch (err) {
      console.error('Erreur création traduction inverse:', err);
      alert('❌ Erreur : ' + err.message);
    }
  };

  // Créer une traduction transitive (via langue intermédiaire)
  // Ex: ES "perro" → EN "dog" via FR "chien"
  const handleCreerTraductionTransitive = async (motSourceId, motCibleId) => {
    try {
      // Créer traduction source → cible
      const response1 = await fetch('/api/traductions', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          motSourceId: motSourceId,
          motCibleId: motCibleId,
        }),
      });

      const data1 = await response1.json();
      if (!data1.success) {
        throw new Error(data1.error || 'Erreur lors de la création de la traduction');
      }

      // Créer traduction inverse cible → source
      const response2 = await fetch('/api/traductions', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          motSourceId: motCibleId,
          motCibleId: motSourceId,
        }),
      });

      const data2 = await response2.json();
      if (!data2.success) {
        throw new Error(data2.error || 'Erreur lors de la création de la traduction inverse');
      }

      alert('✅ Traductions bidirectionnelles créées avec succès');
      onReload();
    } catch (err) {
      console.error('Erreur création traduction transitive:', err);
      alert('❌ Erreur : ' + err.message);
    }
  };

  // Démarrer l'ajout d'un synonyme
  const handleStartAjouterSynonyme = (mot, langueCible) => {
    setAddingSynonymeFor({
      motId: mot.id,
      langueSource: mot.langue,
      langueCible: langueCible,
    });
    setSynonymeFormData({
      motSingulier: '',
      motPluriel: '',
      genre: mot.genre || '',
      type: mot.type || 'nom',
      contexte: '',
    });
  };

  // Annuler l'ajout d'un synonyme
  const handleCancelAjouterSynonyme = () => {
    setAddingSynonymeFor(null);
    setSynonymeFormData({
      motSingulier: '',
      motPluriel: '',
      genre: '',
      type: 'nom',
      contexte: '',
    });
  };

  // Sauvegarder un nouveau synonyme
  const handleSaveSynonyme = async () => {
    if (!synonymeFormData.motSingulier.trim()) {
      alert('❌ Le mot au singulier est requis');
      return;
    }

    try {
      // Log de debug : afficher les informations de contexte
      console.log('🔍 [GestionMots] Création traduction:');
      console.log('  - Mot source ID:', addingSynonymeFor.motId);
      console.log('  - Langue source:', addingSynonymeFor.langueSource);
      console.log('  - Langue cible:', addingSynonymeFor.langueCible);
      console.log('  - Nouveau mot:', synonymeFormData.motSingulier.trim());

      // ⭐ ÉTAPE 1 : Vérifier si une traduction inverse existe déjà
      console.log('🔍 Vérification traduction inverse...');
      const verificationInverseResponse = await fetch(
        `/api/traductions/verifier-inverse?motSourceId=${addingSynonymeFor.motId}&langueCible=${addingSynonymeFor.langueCible}`
      );

      const verificationInverseData = await verificationInverseResponse.json();

      if (!verificationInverseData.success) {
        throw new Error('Erreur lors de la vérification de la traduction inverse');
      }

      if (verificationInverseData.existe) {
        // ✅ UNE TRADUCTION INVERSE EXISTE DÉJÀ !
        const motInverse = verificationInverseData.mot;
        console.log(`🎯 Traduction inverse trouvée: "${motInverse.motSingulier}" (${motInverse.langue}, ID=${motInverse.id})`);
        console.log(`   → Création uniquement de la traduction manquante: ${addingSynonymeFor.motId} → ${motInverse.id}`);

        // Créer seulement la traduction manquante (motSource → motInverse)
        const createTradResponse = await fetch('/api/traductions', {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({
            motSourceId: addingSynonymeFor.motId,
            motCibleId: motInverse.id,
          }),
        });

        const createTradData = await createTradResponse.json();

        if (!createTradData.success) {
          console.error('❌ Échec création traduction:', createTradData);
          throw new Error('Erreur lors de la création de la traduction');
        }

        console.log(`✅ Traduction créée avec succès:`, createTradData.existed ? '(existait déjà)' : '(nouvelle)');

        alert(`✅ Traduction créée automatiquement !\n\nLe mot "${motInverse.motSingulier}" existait déjà en ${motInverse.langue}.\nLa traduction bidirectionnelle a été complétée.`);

        setAddingSynonymeFor(null);
        setSynonymeFormData({
          motSingulier: '',
          motPluriel: '',
          genre: '',
          type: 'nom',
          contexte: '',
        });
        onReload();
        return; // ⚠️ IMPORTANT : On s'arrête ici, pas besoin de créer un nouveau mot !
      }

      console.log('❌ Pas de traduction inverse, vérification existence du mot dans langue cible...');

      // ÉTAPE 2 : Vérifier si le mot existe déjà dans la langue cible
      const verificationResponse = await fetch(
        `/api/mots/verifier-existence?langue=${addingSynonymeFor.langueCible}&motSingulier=${encodeURIComponent(synonymeFormData.motSingulier.trim())}`
      );

      const verificationData = await verificationResponse.json();

      if (!verificationData.success) {
        throw new Error('Erreur lors de la vérification du mot');
      }

      let nouveauMotId;

      if (verificationData.existe) {
        // Le mot existe déjà, on récupère son ID
        nouveauMotId = verificationData.mot.id;
        console.log(`✅ Le mot "${synonymeFormData.motSingulier}" existe déjà avec l'ID ${nouveauMotId}, création des traductions uniquement`);
        console.log(`  - Langue du mot existant: ${verificationData.mot.langue}`);
      } else {
        // Le mot n'existe pas, on le crée
        console.log(`➕ Le mot "${synonymeFormData.motSingulier}" n'existe pas, création du mot et des traductions`);

        const createMotResponse = await fetch('/api/mots', {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({
            ...synonymeFormData,
            langue: addingSynonymeFor.langueCible,
            userId: user.id,
          }),
        });

        const createMotData = await createMotResponse.json();

        if (!createMotData.success) {
          throw new Error(createMotData.error || 'Erreur lors de la création du mot');
        }

        nouveauMotId = createMotData.mot.id;
        console.log(`  - Nouveau mot créé avec ID: ${nouveauMotId}`);
      }

      // ÉTAPE 3 : Créer la traduction source → nouveau mot
      console.log(`🔗 Création traduction 1: ${addingSynonymeFor.motId} (${addingSynonymeFor.langueSource}) → ${nouveauMotId} (${addingSynonymeFor.langueCible})`);
      const createTrad1Response = await fetch('/api/traductions', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          motSourceId: addingSynonymeFor.motId,
          motCibleId: nouveauMotId,
        }),
      });

      const createTrad1Data = await createTrad1Response.json();

      if (!createTrad1Data.success) {
        console.error('❌ Échec traduction 1:', createTrad1Data);
        throw new Error('Erreur lors de la création de la traduction');
      }
      console.log(`✅ Traduction 1 créée:`, createTrad1Data.existed ? '(existait déjà)' : '(nouvelle)');

      // ÉTAPE 4 : Créer la traduction inverse
      console.log(`🔗 Création traduction 2: ${nouveauMotId} (${addingSynonymeFor.langueCible}) → ${addingSynonymeFor.motId} (${addingSynonymeFor.langueSource})`);
      const createTrad2Response = await fetch('/api/traductions', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          motSourceId: nouveauMotId,
          motCibleId: addingSynonymeFor.motId,
        }),
      });

      const createTrad2Data = await createTrad2Response.json();

      if (!createTrad2Data.success) {
        console.error('❌ Échec traduction 2:', createTrad2Data);
        throw new Error('Erreur lors de la création de la traduction inverse');
      }
      console.log(`✅ Traduction 2 créée:`, createTrad2Data.existed ? '(existait déjà)' : '(nouvelle)');

      // Message de succès différent selon si le mot existait déjà ou non
      if (verificationData.existe) {
        alert(`✅ Traductions créées avec succès\n\nLe mot "${synonymeFormData.motSingulier}" existait déjà dans la base de données.\nLes traductions bidirectionnelles ont été ajoutées.`);
      } else {
        alert(`✅ Mot et traductions créés avec succès\n\nLe nouveau mot "${synonymeFormData.motSingulier}" a été ajouté avec ses traductions bidirectionnelles.`);
      }

      setAddingSynonymeFor(null);
      setSynonymeFormData({
        motSingulier: '',
        motPluriel: '',
        genre: '',
        type: 'nom',
        contexte: '',
      });
      onReload();
    } catch (err) {
      console.error('Erreur ajout synonyme:', err);
      alert('❌ Erreur : ' + err.message);
    }
  };

  // Afficher la langue avec drapeau
  const getLangueLabel = (langue) => {
    const langues = {
      fr: '🇫🇷 Français',
      en: '🇬🇧 Anglais',
      es: '🇪🇸 Espagnol',
    };
    return langues[langue] || langue;
  };

  // Regrouper les traductions par langue cible
  const regrouperTraductionsParLangue = (mot) => {
    if (!mot.traductions || mot.traductions.length === 0) return {};

    const groupes = {};

    mot.traductions.forEach((trad) => {
      const motCible = trad.motCible;
      const motSource = trad.motSource;

      if (motSource.id === mot.id) {
        const langueCible = motCible.langue;
        if (!groupes[langueCible]) {
          groupes[langueCible] = [];
        }
        groupes[langueCible].push(trad);
      }
    });

    return groupes;
  };

  return (
    <>
      {/* Chargement */}
      {loading && (
        <div className="loading-state">
          <Loader2 size={48} className="spin" color="#4F46E5" />
          <p>Chargement des mots...</p>
        </div>
      )}

      {/* Erreur */}
      {error && (
        <div className="error-state">
          <X size={32} color="#EF4444" />
          <p>Erreur : {error}</p>
          <button onClick={onReload} className="btn-retry">
            Réessayer
          </button>
        </div>
      )}

      {/* Liste des mots */}
      {!loading && !error && (
        <>
          {mots.length === 0 ? (
            <div className="empty-state">
              <CheckCircle size={48} color="#10B981" />
              <p>{emptyMessage}</p>
              {emptyHint && <p className="empty-hint">{emptyHint}</p>}
            </div>
          ) : (
            <div className="mots-list">
              <p className="mots-count">
                {mots.length} mot{mots.length > 1 ? 's' : ''} trouvé{mots.length > 1 ? 's' : ''}
              </p>

              {mots.map((mot) => (
                <div key={mot.id} className="mot-card">
                  {/* En-tête de la carte */}
                  <div className="mot-card-header">
                    <div className="mot-info">
                      <span className="mot-langue">{getLangueLabel(mot.langue)}</span>
                      <span className="mot-type">{mot.type}</span>
                      {mot.genre && <span className="mot-genre">{mot.genre}</span>}
                    </div>
                    <div className="mot-actions">
                      {editingMot === mot.id ? (
                        <>
                          <button
                            onClick={() => handleSaveEdit(mot.id)}
                            className="btn-action btn-save"
                            title="Sauvegarder"
                          >
                            <Save size={18} />
                          </button>
                          <button
                            onClick={handleCancelEdit}
                            className="btn-action btn-cancel"
                            title="Annuler"
                          >
                            <X size={18} />
                          </button>
                        </>
                      ) : (
                        <>
                          <button
                            onClick={() => handleStartEdit(mot)}
                            className="btn-action btn-edit"
                            title="Modifier"
                          >
                            <Edit size={18} />
                          </button>
                          {showValiderButton && (
                            <button
                              onClick={() => handleMarquerCorrige(mot.id)}
                              className="btn-action btn-validate"
                              title="Marquer comme corrigé"
                            >
                              <CheckCircle size={18} />
                            </button>
                          )}
                          <button
                            onClick={() => handleSupprimerMot(mot.id)}
                            className="btn-action btn-delete"
                            title="Supprimer"
                          >
                            <Trash2 size={18} />
                          </button>
                        </>
                      )}
                    </div>
                  </div>

                  {/* Contenu de la carte */}
                  {editingMot === mot.id ? (
                    // Mode édition
                    <div className="mot-edit-form">
                      <div className="form-row">
                        <label>
                          Singulier:
                          <input
                            type="text"
                            value={formData.motSingulier}
                            onChange={(e) =>
                              setFormData({ ...formData, motSingulier: e.target.value })
                            }
                            autoCapitalize="off"
                          />
                        </label>
                        {!['invariable', 'singularia_tantum', 'pluralia_tantum'].includes(formData.nombre) && (
                          <label>
                            Pluriel:
                            <input
                              type="text"
                              value={formData.motPluriel}
                              onChange={(e) =>
                                setFormData({ ...formData, motPluriel: e.target.value })
                              }
                              autoCapitalize="off"
                            />
                          </label>
                        )}
                      </div>
                      <div className="form-row">
                        <label>
                          Nombre:
                          <select
                            value={formData.nombre || ''}
                            onChange={(e) => setFormData({
                              ...formData,
                              nombre: e.target.value || null,
                              motPluriel: ['invariable', 'singularia_tantum', 'pluralia_tantum'].includes(e.target.value) ? '' : formData.motPluriel
                            })}
                          >
                            <option value="">— Non renseigné —</option>
                            <option value="singulier">Singulier</option>
                            <option value="pluriel">Pluriel</option>
                            <option value="invariable">Invariable</option>
                            <option value="singularia_tantum">Singularia tantum (singulier uniquement)</option>
                            <option value="pluralia_tantum">Pluralia tantum (pluriel uniquement)</option>
                          </select>
                        </label>
                      </div>
                      <div className="form-row">
                        <label>
                          Genre:
                          <select
                            value={formData.genre}
                            onChange={(e) => setFormData({ ...formData, genre: e.target.value })}
                          >
                            <option value="">Aucun</option>
                            <option value="masculin">Masculin</option>
                            <option value="féminin">Féminin</option>
                            <option value="neutre">Neutre</option>
                          </select>
                        </label>
                        <label>
                          Type:
                          <select
                            value={formData.type}
                            onChange={(e) => setFormData({ ...formData, type: e.target.value })}
                          >
                            <option value="nom">Nom</option>
                            <option value="adjectif">Adjectif</option>
                            <option value="adverbe">Adverbe</option>
                            <option value="pronom">Pronom</option>
                            <option value="adjectif_indefini">Adjectif indéfini</option>
                            <option value="expression">Expression</option>
                          </select>
                        </label>
                      </div>
                      <div className="form-row">
                        <label style={{ gridColumn: '1 / -1' }}>
                          Contexte (pour différencier les polysémies):
                          <input
                            list="contextes-list"
                            value={formData.contexte}
                            onChange={(e) => setFormData({ ...formData, contexte: e.target.value })}
                            placeholder="Tapez ou choisissez un contexte..."
                          />
                          <datalist id="contextes-list">
                            {contextes.filter(ctx => ctx.value !== '').map((ctx) => (
                              <option key={ctx.value} value={ctx.value}>
                                {ctx.label}
                              </option>
                            ))}
                          </datalist>
                        </label>
                      </div>
                      <div className="form-row">
                        <label style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                          <input
                            type="checkbox"
                            checked={formData.estFauxAmi || false}
                            onChange={(e) => setFormData({ ...formData, estFauxAmi: e.target.checked })}
                          />
                          ⚠️ Faux ami (existe dans plusieurs langues avec sens différent)
                        </label>
                      </div>
                    </div>
                  ) : (
                    // Mode lecture
                    <div className="mot-details">
                      <div className="mot-word">
                        <strong>{mot.motSingulier}</strong>
                        {mot.estFauxAmi && (
                          <span style={{
                            marginLeft: '8px',
                            padding: '2px 8px',
                            background: '#FEE2E2',
                            color: '#991B1B',
                            borderRadius: '4px',
                            fontSize: '0.85em',
                            fontWeight: '600'
                          }}>
                            ⚠️ Faux ami
                          </span>
                        )}
                        {mot.contexte && mot.contexte !== '' && (
                          <span className="mot-context"> ({mot.contexte})</span>
                        )}
                        {mot.motPluriel && <span className="mot-plural"> / {mot.motPluriel}</span>}
                      </div>

                      {/* Traductions groupées par langue */}
                      {mot.traductions && mot.traductions.length > 0 && (() => {
                        const groupes = regrouperTraductionsParLangue(mot);

                        return (
                          <div className="mot-traductions">
                            {Object.keys(groupes).map((langueCible) => {
                              const traductions = groupes[langueCible];
                              const nbSynonymes = traductions.length;

                              return (
                                <div key={langueCible} className="langue-groupe">
                                  <p className="traductions-title">
                                    {getLangueLabel(mot.langue)} → {getLangueLabel(langueCible)}
                                    {nbSynonymes > 1 && (
                                      <span className="synonymes-count"> ({nbSynonymes} synonymes)</span>
                                    )}
                                  </p>

                                  <ul>
                                    {traductions.map((trad) => {
                                      const motSource = trad.motSource;
                                      const motCible = trad.motCible;

                                      const traductionInverseExiste = mot.traductions.some(
                                        (t) => t.motSourceId === motCible.id && t.motCibleId === motSource.id
                                      );

                                      return (
                                        <li key={trad.id} className="traduction-item">
                                          {editingTraduction === trad.id ? (
                                            // Mode édition traduction
                                            <div className="traduction-edit-form">
                                              <div className="form-row-inline">
                                                <label>
                                                  Singulier:
                                                  <input
                                                    type="text"
                                                    value={traductionFormData.motSingulier}
                                                    onChange={(e) =>
                                                      setTraductionFormData({ ...traductionFormData, motSingulier: e.target.value })
                                                    }
                                                    autoCapitalize="off"
                                                  />
                                                </label>
                                                <label>
                                                  Pluriel:
                                                  <input
                                                    type="text"
                                                    value={traductionFormData.motPluriel}
                                                    onChange={(e) =>
                                                      setTraductionFormData({ ...traductionFormData, motPluriel: e.target.value })
                                                    }
                                                    autoCapitalize="off"
                                                  />
                                                </label>
                                                <label>
                                                  Genre:
                                                  <select
                                                    value={traductionFormData.genre}
                                                    onChange={(e) =>
                                                      setTraductionFormData({ ...traductionFormData, genre: e.target.value })
                                                    }
                                                  >
                                                    <option value="">Aucun</option>
                                                    <option value="masculin">Masculin</option>
                                                    <option value="féminin">Féminin</option>
                                                    <option value="neutre">Neutre</option>
                                                  </select>
                                                </label>
                                              </div>
                                              <div className="traduction-actions">
                                                <button
                                                  onClick={() => handleSaveEditTraduction(motCible.id)}
                                                  className="btn-action btn-save"
                                                  title="Sauvegarder"
                                                >
                                                  <Save size={16} /> Sauvegarder
                                                </button>
                                                <button
                                                  onClick={handleCancelEditTraduction}
                                                  className="btn-action btn-cancel"
                                                  title="Annuler"
                                                >
                                                  <X size={16} /> Annuler
                                                </button>
                                              </div>
                                            </div>
                                          ) : (
                                            // Mode lecture traduction
                                            <>
                                              <div className="traduction-display">
                                                <span className="traduction-text">
                                                  <strong>{motCible.motSingulier}</strong>
                                                  {motCible.contexte && motCible.contexte !== '' && (
                                                    <span className="mot-context"> ({motCible.contexte})</span>
                                                  )}
                                                  {motCible.genre && (
                                                    <span className="mot-genre-badge"> {motCible.genre === 'masculin' ? '♂️' : '♀️'}</span>
                                                  )}
                                                </span>
                                                <span className="traduction-actions">
                                                  <button
                                                    onClick={() => handleStartEditTraduction(trad.id, motCible)}
                                                    className="btn-action btn-edit-small"
                                                    title="Modifier"
                                                  >
                                                    <Edit size={14} />
                                                  </button>
                                                  <button
                                                    onClick={() => handleSupprimerTraduction(trad.id)}
                                                    className="btn-action btn-delete-small"
                                                    title="Supprimer"
                                                  >
                                                    <Trash2 size={14} />
                                                  </button>
                                                </span>
                                              </div>
                                              {!traductionInverseExiste && (
                                                <div className="traduction-warning">
                                                  <span className="warning-text">
                                                    ⚠️ Traduction inverse manquante
                                                  </span>
                                                  <button
                                                    onClick={() => handleCreerTraductionInverse(motSource.id, motCible.id)}
                                                    className="btn-action btn-create-inverse"
                                                    title="Créer la traduction inverse"
                                                  >
                                                    ➕ Créer traduction inverse
                                                  </button>
                                                </div>
                                              )}
                                            </>
                                          )}
                                        </li>
                                      );
                                    })}

                                    {/* Formulaire d'ajout de synonyme */}
                                    {addingSynonymeFor && addingSynonymeFor.motId === mot.id && addingSynonymeFor.langueCible === langueCible ? (
                                      <li className="traduction-item add-synonyme-form">
                                        <div className="synonyme-form">
                                          <p style={{ fontWeight: '600', marginBottom: '8px' }}>Nouveau synonyme :</p>
                                          <div className="form-row-inline">
                                            <label>
                                              Singulier:
                                              <input
                                                type="text"
                                                value={synonymeFormData.motSingulier}
                                                onChange={(e) =>
                                                  setSynonymeFormData({ ...synonymeFormData, motSingulier: e.target.value })
                                                }
                                                placeholder="Ex: caillou"
                                                autoCapitalize="off"
                                              />
                                            </label>
                                            <label>
                                              Genre:
                                              <select
                                                value={synonymeFormData.genre}
                                                onChange={(e) =>
                                                  setSynonymeFormData({ ...synonymeFormData, genre: e.target.value })
                                                }
                                              >
                                                <option value="">Aucun</option>
                                                <option value="masculin">Masculin</option>
                                                <option value="féminin">Féminin</option>
                                                <option value="neutre">Neutre</option>
                                              </select>
                                            </label>
                                            <label>
                                              Contexte:
                                              <input
                                                list="contextes-list-synonyme"
                                                value={synonymeFormData.contexte}
                                                onChange={(e) =>
                                                  setSynonymeFormData({ ...synonymeFormData, contexte: e.target.value })
                                                }
                                                placeholder="Tapez ou choisissez..."
                                              />
                                              <datalist id="contextes-list-synonyme">
                                                {contextes.filter(ctx => ctx.value !== '').map((ctx) => (
                                                  <option key={ctx.value} value={ctx.value}>
                                                    {ctx.label}
                                                  </option>
                                                ))}
                                              </datalist>
                                            </label>
                                          </div>
                                          <div className="traduction-actions">
                                            <button
                                              onClick={handleSaveSynonyme}
                                              className="btn-action btn-save"
                                              title="Sauvegarder"
                                            >
                                              <Save size={16} /> Sauvegarder
                                            </button>
                                            <button
                                              onClick={handleCancelAjouterSynonyme}
                                              className="btn-action btn-cancel"
                                              title="Annuler"
                                            >
                                              <X size={16} /> Annuler
                                            </button>
                                          </div>
                                        </div>
                                      </li>
                                    ) : (
                                      <li className="traduction-item add-synonyme-btn">
                                        <button
                                          onClick={() => handleStartAjouterSynonyme(mot, langueCible)}
                                          className="btn-action btn-add-synonyme"
                                          style={{
                                            width: '100%',
                                            padding: '8px 16px',
                                            backgroundColor: '#F0FDF4',
                                            color: '#166534',
                                            border: '2px dashed #86EFAC',
                                            borderRadius: '6px',
                                            cursor: 'pointer',
                                            fontSize: '14px',
                                            fontWeight: '500',
                                          }}
                                        >
                                          ➕ Ajouter un synonyme
                                        </button>
                                      </li>
                                    )}
                                  </ul>
                                </div>
                              );
                            })}

                            {/* Si une langue cible manquante est spécifiée et qu'elle n'est pas dans les groupes */}
                            {langueCibleManquante && !Object.keys(groupes).includes(langueCibleManquante) && (
                              <div className="langue-groupe traduction-manquante">
                                {/* ⭐ CAS 1: Traduction inverse existe -> bouton création automatique */}
                                {mot.traductionInverseExistante ? (
                                  <div className="traduction-inverse-existante">
                                    <span className="info-text" style={{ color: '#059669', fontWeight: '500' }}>
                                      🔄 Traduction inverse trouvée : <strong>{mot.traductionInverseExistante.motSource.motSingulier}</strong> ({getLangueLabel(langueCibleManquante)})
                                    </span>
                                    <button
                                      onClick={() => handleCreerTraductionInverse(mot.traductionInverseExistante.motSourceId, mot.id)}
                                      className="btn-action btn-create-inverse"
                                      title="Créer automatiquement la traduction bidirectionnelle"
                                      style={{
                                        marginTop: '8px',
                                        backgroundColor: '#10B981',
                                        color: 'white',
                                      }}
                                    >
                                      ✨ Créer automatiquement
                                    </button>
                                  </div>
                                ) : mot.traductionTransitive ? (
                                  /* ⭐ CAS 2: Traduction transitive existe -> suggestion via langue intermédiaire */
                                  <div className="traduction-transitive-existante">
                                    <span className="info-text" style={{ color: '#7C3AED', fontWeight: '500' }}>
                                      💡 Suggestion via {getLangueLabel(mot.traductionTransitive.langueIntermediaire)} :
                                    </span>
                                    <div style={{ marginTop: '4px', fontSize: '0.9em', color: '#6B7280' }}>
                                      {getLangueLabel(mot.traductionTransitive.langueIntermediaire)} "<strong>{mot.traductionTransitive.motIntermediaire.motSingulier}</strong>"
                                      → {getLangueLabel(mot.langue)} "<strong>{mot.motSingulier}</strong>"
                                      <br />
                                      {getLangueLabel(mot.traductionTransitive.langueIntermediaire)} "<strong>{mot.traductionTransitive.motIntermediaire.motSingulier}</strong>"
                                      → {getLangueLabel(langueCibleManquante)} "<strong>{mot.traductionTransitive.motCible.motSingulier}</strong>"
                                    </div>
                                    <div style={{ marginTop: '8px', padding: '8px', background: '#F3E8FF', borderRadius: '6px' }}>
                                      <span style={{ fontWeight: '600' }}>
                                        ➡️ Créer : {mot.motSingulier} → {mot.traductionTransitive.motCible.motSingulier}
                                      </span>
                                    </div>
                                    <button
                                      onClick={() => handleCreerTraductionTransitive(mot.id, mot.traductionTransitive.motCible.id)}
                                      className="btn-action btn-create-inverse"
                                      title="Créer la traduction transitive"
                                      style={{
                                        marginTop: '8px',
                                        backgroundColor: '#7C3AED',
                                        color: 'white',
                                      }}
                                    >
                                      ✨ Créer cette traduction
                                    </button>
                                  </div>
                                ) : addingSynonymeFor && addingSynonymeFor.motId === mot.id && addingSynonymeFor.langueCible === langueCibleManquante ? (
                                  // CAS 2: Formulaire d'ajout de traduction manuelle
                                  <div className="synonyme-form">
                                    <p style={{ fontWeight: '600', marginBottom: '8px' }}>Nouvelle traduction :</p>
                                    <div className="form-row-inline">
                                      <label>
                                        Singulier:
                                        <input
                                          type="text"
                                          value={synonymeFormData.motSingulier}
                                          onChange={(e) =>
                                            setSynonymeFormData({ ...synonymeFormData, motSingulier: e.target.value })
                                          }
                                          placeholder="Ex: colegio"
                                          autoCapitalize="off"
                                        />
                                      </label>
                                      <label>
                                        Genre:
                                        <select
                                          value={synonymeFormData.genre}
                                          onChange={(e) =>
                                            setSynonymeFormData({ ...synonymeFormData, genre: e.target.value })
                                          }
                                        >
                                          <option value="">Aucun</option>
                                          <option value="masculin">Masculin</option>
                                          <option value="féminin">Féminin</option>
                                          <option value="neutre">Neutre</option>
                                        </select>
                                      </label>
                                      <label>
                                        Contexte:
                                        <select
                                          value={synonymeFormData.contexte}
                                          onChange={(e) =>
                                            setSynonymeFormData({ ...synonymeFormData, contexte: e.target.value })
                                          }
                                        >
                                          {contextes.map((ctx) => (
                                            <option key={ctx.value} value={ctx.value}>
                                              {ctx.label}
                                            </option>
                                          ))}
                                        </select>
                                      </label>
                                    </div>
                                    <div className="traduction-actions">
                                      <button
                                        onClick={handleSaveSynonyme}
                                        className="btn-action btn-save"
                                        title="Sauvegarder"
                                      >
                                        <Save size={16} /> Sauvegarder
                                      </button>
                                      <button
                                        onClick={handleCancelAjouterSynonyme}
                                        className="btn-action btn-cancel"
                                        title="Annuler"
                                      >
                                        <X size={16} /> Annuler
                                      </button>
                                    </div>
                                  </div>
                                ) : (
                                  // CAS 3: Bouton pour ajouter une traduction manuellement
                                  <div className="traduction-warning-major">
                                    <span className="warning-text">
                                      ⚠️ Aucune traduction vers {getLangueLabel(langueCibleManquante)}
                                    </span>
                                    <button
                                      onClick={() => handleStartAjouterSynonyme(mot, langueCibleManquante)}
                                      className="btn-action btn-create-inverse"
                                      title="Ajouter une traduction"
                                      style={{
                                        marginTop: '8px',
                                      }}
                                    >
                                      ➕ Ajouter une traduction
                                    </button>
                                  </div>
                                )}
                              </div>
                            )}
                          </div>
                        );
                      })()}

                      {/* Si le mot n'a AUCUNE traduction mais qu'une langue cible manquante est spécifiée */}
                      {langueCibleManquante && (!mot.traductions || mot.traductions.length === 0) && (
                        <div className="mot-traductions">
                          <div className="langue-groupe traduction-manquante">
                            {/* ⭐ CAS 1: Traduction inverse existe -> bouton création automatique */}
                            {mot.traductionInverseExistante ? (
                              <div className="traduction-inverse-existante">
                                <span className="info-text" style={{ color: '#059669', fontWeight: '500' }}>
                                  🔄 Traduction inverse trouvée : <strong>{mot.traductionInverseExistante.motSource.motSingulier}</strong> ({getLangueLabel(langueCibleManquante)})
                                </span>
                                <button
                                  onClick={() => handleCreerTraductionInverse(mot.traductionInverseExistante.motSourceId, mot.id)}
                                  className="btn-action btn-create-inverse"
                                  title="Créer automatiquement la traduction bidirectionnelle"
                                  style={{
                                    marginTop: '8px',
                                    backgroundColor: '#10B981',
                                    color: 'white',
                                  }}
                                >
                                  ✨ Créer automatiquement
                                </button>
                              </div>
                            ) : mot.traductionTransitive ? (
                              /* ⭐ CAS 2: Traduction transitive existe -> suggestion via langue intermédiaire */
                              <div className="traduction-transitive-existante">
                                <span className="info-text" style={{ color: '#7C3AED', fontWeight: '500' }}>
                                  💡 Suggestion via {getLangueLabel(mot.traductionTransitive.langueIntermediaire)} :
                                </span>
                                <div style={{ marginTop: '4px', fontSize: '0.9em', color: '#6B7280' }}>
                                  {getLangueLabel(mot.traductionTransitive.langueIntermediaire)} "<strong>{mot.traductionTransitive.motIntermediaire.motSingulier}</strong>"
                                  → {getLangueLabel(mot.langue)} "<strong>{mot.motSingulier}</strong>"
                                  <br />
                                  {getLangueLabel(mot.traductionTransitive.langueIntermediaire)} "<strong>{mot.traductionTransitive.motIntermediaire.motSingulier}</strong>"
                                  → {getLangueLabel(langueCibleManquante)} "<strong>{mot.traductionTransitive.motCible.motSingulier}</strong>"
                                </div>
                                <div style={{ marginTop: '8px', padding: '8px', background: '#F3E8FF', borderRadius: '6px' }}>
                                  <span style={{ fontWeight: '600' }}>
                                    ➡️ Créer : {mot.motSingulier} → {mot.traductionTransitive.motCible.motSingulier}
                                  </span>
                                </div>
                                <button
                                  onClick={() => handleCreerTraductionTransitive(mot.id, mot.traductionTransitive.motCible.id)}
                                  className="btn-action btn-create-inverse"
                                  title="Créer la traduction transitive"
                                  style={{
                                    marginTop: '8px',
                                    backgroundColor: '#7C3AED',
                                    color: 'white',
                                  }}
                                >
                                  ✨ Créer cette traduction
                                </button>
                              </div>
                            ) : addingSynonymeFor && addingSynonymeFor.motId === mot.id && addingSynonymeFor.langueCible === langueCibleManquante ? (
                              // CAS 2: Formulaire d'ajout de traduction manuelle
                              <div className="synonyme-form">
                                <p style={{ fontWeight: '600', marginBottom: '8px' }}>Nouvelle traduction :</p>
                                <div className="form-row-inline">
                                  <label>
                                    Singulier:
                                    <input
                                      type="text"
                                      value={synonymeFormData.motSingulier}
                                      onChange={(e) =>
                                        setSynonymeFormData({ ...synonymeFormData, motSingulier: e.target.value })
                                      }
                                      placeholder="Ex: colegio"
                                      autoCapitalize="off"
                                    />
                                  </label>
                                  <label>
                                    Genre:
                                    <select
                                      value={synonymeFormData.genre}
                                      onChange={(e) =>
                                        setSynonymeFormData({ ...synonymeFormData, genre: e.target.value })
                                      }
                                    >
                                      <option value="">Aucun</option>
                                      <option value="masculin">Masculin</option>
                                      <option value="féminin">Féminin</option>
                                      <option value="neutre">Neutre</option>
                                    </select>
                                  </label>
                                  <label>
                                    Contexte:
                                    <select
                                      value={synonymeFormData.contexte}
                                      onChange={(e) =>
                                        setSynonymeFormData({ ...synonymeFormData, contexte: e.target.value })
                                      }
                                    >
                                      {contextes.map((ctx) => (
                                        <option key={ctx.value} value={ctx.value}>
                                          {ctx.label}
                                        </option>
                                      ))}
                                    </select>
                                  </label>
                                </div>
                                <div className="traduction-actions">
                                  <button
                                    onClick={handleSaveSynonyme}
                                    className="btn-action btn-save"
                                    title="Sauvegarder"
                                  >
                                    <Save size={16} /> Sauvegarder
                                  </button>
                                  <button
                                    onClick={handleCancelAjouterSynonyme}
                                    className="btn-action btn-cancel"
                                    title="Annuler"
                                  >
                                    <X size={16} /> Annuler
                                  </button>
                                </div>
                              </div>
                            ) : (
                              // CAS 3: Bouton pour ajouter une traduction manuellement
                              <div className="traduction-warning-major">
                                <span className="warning-text">
                                  ⚠️ Aucune traduction vers {getLangueLabel(langueCibleManquante)}
                                </span>
                                <button
                                  onClick={() => handleStartAjouterSynonyme(mot, langueCibleManquante)}
                                  className="btn-action btn-create-inverse"
                                  title="Ajouter une traduction"
                                  style={{
                                    marginTop: '8px',
                                  }}
                                >
                                  ➕ Ajouter une traduction
                                </button>
                              </div>
                            )}
                          </div>
                        </div>
                      )}
                    </div>
                  )}
                </div>
              ))}
            </div>
          )}
        </>
      )}
    </>
  );
}
